import React, { useState, useImperativeHandle, forwardRef } from 'react';
import { Modal, Table, Button, Tooltip, Tag } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { FileTextOutlined, EyeOutlined } from '@ant-design/icons';
import { makePostCall } from '../../utils/helper';
import { getStatusStyle } from '../../utils/sharedTableColumns';
import { getPacsStatusLabel, getPatientIdLabel } from '../../constants/fieldLabels';

export interface PatientOrdersModalRef {
  open: (record: any) => void;
}

interface ModalState {
  visible: boolean;
  patientName: string;
  patPin: string;
  orders: any[];
  loading: boolean;
}

const INITIAL_STATE: ModalState = {
  visible: false,
  patientName: '',
  patPin: '',
  orders: [],
  loading: false,
};

interface Props {
  onOpenReport: (pseudoRecord: any) => void;
  onOpenViewer: (pseudoRecord: any) => void;
  onViewReport: (pseudoRecord: any) => void;
}

const buildColumns = (
  onOpenReport: Props['onOpenReport'],
  onOpenViewer: Props['onOpenViewer'],
  onViewReport: Props['onViewReport'],
): ColumnsType<any> => [
  {
    title: 'S.No',
    width: 50,
    render: (_: any, __: any, index: number) => index + 1,
  },
  {
    title: 'Patient Name',
    width: 200,
    ellipsis: true,
    render: (_: any, rec: any) => rec?.patient?.pat_name || '-',
  },
  {
    title: 'Study Description',
    width: 180,
    ellipsis: true,
    render: (_: any, rec: any) => rec?.po_diag_desc || '-',
  },
  {
    title: 'Modality',
    width: 80,
    render: (_: any, rec: any) => rec?.po_modality || '-',
  },
  {
    title: 'Status',
    // Fits the shared badge's widest label ("UNREPORTED").
    width: 124,
    render: (_: any, rec: any) => {
      const status: string = rec?.order_workflow?.ow_pacs_status;
      if (!status) return <span>-</span>;
      const isSignedoff = status==='SIGNEDOFF';
      return (
          <Tag
            style={{...getStatusStyle(status),cursor: isSignedoff ? 'pointer' : 'default',}}
            onClick={(e) => {
              if (isSignedoff){
                e.stopPropagation();
                onViewReport(rec)
                };
            }}
          >
            {getPacsStatusLabel(status)}
          </Tag>
      );
    },
  },
  {
    title: 'Scan Date/Time',
    width: 155,
    ellipsis: true,
    render: (_: any, rec: any) =>
      rec?.pacs_study?.ps_study_dt_tm ? (
        new Date(rec.pacs_study.ps_study_dt_tm).toLocaleString('en-IN', {
          day: '2-digit',
          month: 'short',
          year: 'numeric',
          hour: '2-digit',
          minute: '2-digit',
        })
      ) : (
        <span style={{ color: '#b0b0b0' }}>Not scanned</span>
      ),
  },
  {
    title: 'Open Report',
    width: 55,
    render: (_: any, rec: any) => {
      const hasDicom = !!rec?.pacs_study?.ps_study_uid;
      const isSignedOff = rec?.order_workflow?.ow_pacs_status === 'SIGNEDOFF';
      const disabled = !hasDicom || !isSignedOff;
      return (
        <Tooltip title={disabled ? (hasDicom ? 'Not signed off yet' : 'No DICOM study') : 'Open Report'}>
          <Button
            type="text"
            icon={
              <FileTextOutlined style={{ fontSize: '18px', color: disabled ? '#ccc' : '#1890ff' }} />
            }
            disabled={disabled}
            onClick={() => {
              onOpenReport({
                // viewReport() reads oo_doc_id at the top level to fetch the PDF —
                // forward it from the order so signed-off prior reports open.
                oo_doc_id: rec.oo_doc_id,
                pacs_order: {
                  pacs_ord_id: rec.pacs_ord_id,
                  patient: {
                    pat_pacs_id: rec.patient?.pat_pacs_id,
                    pat_pin: rec.patient?.pat_pin,
                  },
                },
                isFromPatientOrders: true,
              });
            }}
          />
        </Tooltip>
      );
    },
  },
  {
    title: 'Open Viewer',
    width: 55,
    render: (_: any, rec: any) => {
      const studyData = rec?.pacs_study;
      const hasDicom = !!studyData?.ps_study_uid;
      return (
        <Tooltip title={hasDicom ? 'Open Viewer' : 'No DICOM study'}>
          <Button
            type="text"
            icon={
              <EyeOutlined style={{ fontSize: '18px', color: !hasDicom ? '#ccc' : '#52c41a' }} />
            }
            disabled={!hasDicom}
            onClick={() => onOpenViewer({ ps_study_uid: studyData?.ps_study_uid })}
          />
        </Tooltip>
      );
    },
  },
];

const PatientOrdersModal = forwardRef<PatientOrdersModalRef, Props>(
  ({ onOpenReport, onOpenViewer,onViewReport }, ref) => {
    const [modalState, setModalState] = useState<ModalState>(INITIAL_STATE);

    useImperativeHandle(ref, () => ({
      open: async (record: any) => {
        // Study-rooted rows (PACS list) nest the patient under pacs_order;
        // order-rooted rows (Dispatch / Consultant list) carry it at the top level.
        const patient = record?.pacs_order?.patient ?? record?.patient;
        const patPin = patient?.pat_pin;
        const patientName = patient?.pat_name || '';
        if (!patPin) return;

        setModalState({ visible: true, patientName, patPin, orders: [], loading: true });

        try {
          // /patient-order-history starts from pacs_orders (complete list) and LEFT JOINs
          // order_workflow + pacs_studies — so ALL orders are returned regardless of age or scan status
          const res = await makePostCall('/patient-order-history', { pat_pin: patPin });
          setModalState(prev => ({ ...prev, orders: res.data?.data || [], loading: false }));
        } catch (e) {
          console.error('Failed to fetch patient orders', e);
          setModalState(prev => ({ ...prev, orders: [], loading: false }));
        }
      },
    }));

    const columns = React.useMemo(
      () => buildColumns(onOpenReport, onOpenViewer,onViewReport),
      [onOpenReport, onOpenViewer,onViewReport],
    );
    const handleClose = () => setModalState(INITIAL_STATE);

    return (
      <Modal
        title={
          <span>
            Orders for <strong>{modalState.patientName}</strong>
            {modalState.patPin && (
              <span style={{ fontWeight: 400, color: '#888', marginLeft: 8, fontSize: 13 }}>
                ({getPatientIdLabel()}: {modalState.patPin})
              </span>
            )}
          </span>
        }
        open={modalState.visible}
        onCancel={handleClose}
        footer={null}
        width={1500}
        style={{ top: 35 }}
        styles={{ body: { maxHeight: '70vh', overflowY: 'auto' } }}
      >
        <Table
          className="patient-orders-table"
          size="small"
          loading={modalState.loading}
          dataSource={modalState.orders}
          rowKey={rec => rec?.pacs_ord_id || Math.random()}
          style={{ boxShadow: 'none' }}
          pagination={{ pageSize: 10, showSizeChanger: false }}
          columns={columns}
          locale={{ emptyText: 'No orders found for this patient' }}
        />
      </Modal>
    );
  },
);

PatientOrdersModal.displayName = 'PatientOrdersModal';

export default PatientOrdersModal;
